import { useState, useEffect, useMemo } from 'react';
import { useApp } from '../context';
import {
  Search,
  Heart,
  Briefcase,
  Users,
  GraduationCap,
  Wine,
  Contact as ContactIcon,
  Star,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { cn, formatDate } from '../lib/utils';
import ContactAvatar from './ContactAvatar';

function getHealthRingColor(score: number): string {
  if (score >= 80) return '#10b981'; // green
  if (score >= 50) return '#f59e0b'; // amber
  return '#ef4444'; // red
}

const DEFAULT_FILTERS = ['All', 'Inner Circle', 'Family', 'Work', 'Friends', 'Mentors', 'Dates'];
const DEFAULT_FILTERS_LOWER = new Set(DEFAULT_FILTERS.map((f) => f.toLowerCase()));

export default function ContactList() {
  const { state } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(searchParams.get('filter') || 'All');
  const filters = useMemo(() => {
    const seen = new Set(DEFAULT_FILTERS_LOWER);
    const userTags: string[] = [];
    state.contacts.forEach((c) => {
      c.tags?.forEach((tag) => {
        if (!seen.has(tag.toLowerCase())) {
          seen.add(tag.toLowerCase());
          userTags.push(tag);
        }
      });
    });
    return [...DEFAULT_FILTERS, ...userTags];
  }, [state.contacts]);

  // Sync state FROM URL on mount or URL change
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam && filterParam !== activeFilter) {
      setActiveFilter(filterParam);
    }
  }, [searchParams]);

  // Sync URL FROM state when local filter button clicked
  useEffect(() => {
    if (activeFilter === 'All') {
      searchParams.delete('filter');
    } else {
      searchParams.set('filter', activeFilter);
    }
    setSearchParams(searchParams, { replace: true });
  }, [activeFilter, setSearchParams]);

  const filteredContacts = state.contacts.filter((contact) => {
    const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;
    if (activeFilter === 'All') return true;

    if (activeFilter === 'Inner Circle') {
      return contact.innerCircle === true;
    }

    // Map plural filter tabs to singular relationship types
    const filterMapping: Record<string, string> = {
      Friends: 'Friend',
      Mentors: 'Mentor',
      Dates: 'Date',
    };
    const targetType = filterMapping[activeFilter] || activeFilter;

    // Match on relationshipType OR tags (case-insensitive)
    const matchesRelType = contact.relationshipType?.toLowerCase() === targetType.toLowerCase();
    const matchesTag = contact.tags?.some((t) => t.toLowerCase() === targetType.toLowerCase());
    return matchesRelType || matchesTag;
  });

  const getEmptyStateIcon = () => {
    switch (activeFilter) {
      case 'Family':
        return <Heart size={48} className="text-[#fa8585]/30 mb-4" />;
      case 'Inner Circle':
        return <Star size={48} className="text-[#fa8585]/30 mb-4" />;
      case 'Work':
        return <Briefcase size={48} className="text-[#fa8585]/30 mb-4" />;
      case 'Friends':
        return <Users size={48} className="text-[#fa8585]/30 mb-4" />;
      case 'Mentors':
        return <GraduationCap size={48} className="text-[#fa8585]/30 mb-4" />;
      case 'Dates':
        return <Wine size={48} className="text-[#fa8585]/30 mb-4" />;
      default:
        return <ContactIcon size={48} className="text-[#fa8585]/30 mb-4" />;
    }
  };

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#f8f5f5]/80 backdrop-blur-md px-5 pt-5 pb-2">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">Contacts</h1>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative group">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#fa8585]"
            size={20}
          />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border-none rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-[#fa8585] outline-none transition-all shadow-sm"
          />
        </div>

        {/* Filter Tags */}
        <div className="flex gap-2 overflow-x-auto py-4 no-scrollbar">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={cn(
                'px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all',
                activeFilter === filter
                  ? 'bg-[#fa8585] text-white shadow-sm shadow-[#fa8585]/20'
                  : 'bg-white border border-slate-200 text-slate-600',
              )}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      {/* Contact List */}
      <div className="px-5 space-y-5">
        {/* Overdue Section */}
        {filteredContacts.filter((c) => c.status === 'overdue').length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3 px-1">
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400">
                Needs Attention
              </h2>
              <span className="text-[10px] font-bold bg-[#fa8585]/10 text-[#fa8585] px-2 py-0.5 rounded-full">
                {filteredContacts.filter((c) => c.status === 'overdue').length} OVERDUE
              </span>
            </div>
            <div className="space-y-2">
              {filteredContacts
                .filter((c) => c.status === 'overdue')
                .map((contact) => (
                  <Link
                    key={contact.id}
                    to={`/contact/${contact.id}`}
                    className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm border border-slate-100/50 active:scale-[0.98] transition-transform"
                  >
                    <div className="relative">
                      <ContactAvatar photoUrl={contact.photoUrl} name={contact.name} size="md" />
                      <div className="absolute bottom-0 right-0 w-4 h-4 rounded-full bg-red-500 border-2 border-white"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 truncate">{contact.name}</h3>
                      <p className="text-xs text-slate-500">
                        Last interaction: {formatDate(contact.lastInteractionDate) || 'N/A'}
                      </p>
                      <p className="text-xs font-bold text-red-500 mt-0.5">
                        Overdue: {contact.overdueDays} days
                      </p>
                    </div>
                    <div className="relative size-12 flex items-center justify-center shrink-0">
                      <svg className="w-12 h-12 transform -rotate-90">
                        <circle
                          cx="24"
                          cy="24"
                          r="20"
                          fill="transparent"
                          stroke="#f1f5f9"
                          strokeWidth="4"
                        />
                        {contact.healthScore >= 0 && (
                          <circle
                            cx="24"
                            cy="24"
                            r="20"
                            fill="transparent"
                            stroke={getHealthRingColor(contact.healthScore)}
                            strokeDasharray="125.66"
                            strokeDashoffset={125.66 * (1 - contact.healthScore / 100)}
                            strokeWidth="4"
                            strokeLinecap="round"
                          />
                        )}
                      </svg>
                      <span className="absolute text-xs font-bold text-slate-700">
                        {contact.healthScore >= 0 ? contact.healthScore : 'N/A'}
                      </span>
                    </div>
                  </Link>
                ))}
            </div>
          </section>
        )}

        {/* Other Contacts Section */}
        {filteredContacts.filter((c) => c.status !== 'overdue').length > 0 && (
          <section>
            <div className="space-y-2">
              {filteredContacts
                .filter((c) => c.status !== 'overdue')
                .map((contact) => (
                  <Link
                    key={contact.id}
                    to={`/contact/${contact.id}`}
                    className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm border border-slate-100/50 active:scale-[0.98] transition-transform"
                  >
                    <div className="relative">
                      <ContactAvatar photoUrl={contact.photoUrl} name={contact.name} size="md" />
                      <div
                        className={cn(
                          'absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white',
                          contact.status === 'new'
                            ? 'bg-slate-300'
                            : contact.status === 'needs_attention'
                              ? 'bg-amber-400'
                              : 'bg-emerald-500',
                        )}
                      ></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 truncate">{contact.name}</h3>
                      <p className="text-xs text-slate-500">
                        Last interaction: {formatDate(contact.lastInteractionDate) || 'N/A'}
                      </p>
                      <p
                        className={cn(
                          'text-xs font-medium mt-0.5',
                          contact.status === 'new'
                            ? 'text-slate-400'
                            : contact.status === 'needs_attention'
                              ? 'text-amber-600'
                              : 'text-emerald-600',
                        )}
                      >
                        {contact.status === 'new'
                          ? 'New contact'
                          : contact.status === 'needs_attention'
                            ? `Check in within ${contact.overdueDays} days`
                            : 'All set!'}
                      </p>
                    </div>
                    <div className="relative size-12 flex items-center justify-center shrink-0">
                      <svg className="w-12 h-12 transform -rotate-90">
                        <circle
                          cx="24"
                          cy="24"
                          r="20"
                          fill="transparent"
                          stroke="#f1f5f9"
                          strokeWidth="4"
                        />
                        {contact.healthScore >= 0 && (
                          <circle
                            cx="24"
                            cy="24"
                            r="20"
                            fill="transparent"
                            stroke={getHealthRingColor(contact.healthScore)}
                            strokeDasharray="125.66"
                            strokeDashoffset={125.66 * (1 - contact.healthScore / 100)}
                            strokeWidth="4"
                            strokeLinecap="round"
                          />
                        )}
                      </svg>
                      <span className="absolute text-xs font-bold text-slate-700">
                        {contact.healthScore >= 0 ? contact.healthScore : 'N/A'}
                      </span>
                    </div>
                  </Link>
                ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {filteredContacts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            {getEmptyStateIcon()}
            <h3 className="text-lg font-bold text-slate-900 mb-2">No {activeFilter} contacts</h3>
            <p className="text-slate-500 text-sm">
              {searchQuery
                ? `We couldn't find anyone matching "${searchQuery}" in this category.`
                : `You haven't added any ${activeFilter.toLowerCase()} to your network yet.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
